import { ChevronDown, MapPin, Banknote, Shield } from 'lucide-react';
import React, { useEffect, useState } from "react";
import {
  Link,
  useParams,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import ListingDetails from "./listingitem.jsx";
import RecommendedListingDetails from "./recommendedListingItem.jsx";
import ListingDetailsMap from "./listingmap.js";
import ListingSidebar from "./listingsidebar.js";


import {
  getRecommendation,
  getOfficers,
} from "../../../../../../services/staffingService";





import JobStatistics from "./job_statistics/statistics.job";
import DashboardSidebar from "../../../agencyDashboardSidebar.jsx";
import Header from "../../../agencyHeader.jsx";



// Custom Dropdown Component to replace RSuite Dropdown
const CustomDropdown = ({ title, children, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-3 py-1 text-xs text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
      >
        {title}
        <ChevronDown className="!text-sm" />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 bg-white border border-gray-200 rounded-md shadow-lg min-w-max">
            {React.Children.map(children, (child) => {
              if (!child) return null;
              return React.cloneElement(child, {
                onClick: () => {
                  child.props.onClick?.();
                  setIsOpen(false);
                }
              });
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Custom Dropdown Item Component
const CustomDropdownItem = ({ children, onClick, className = "" }) => {
  return (
    <button
      onClick={onClick}
      className={`block w-full px-3 py-2 text-left text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors ${className}`}
    >
      {children}
    </button>
  );
};

const Listing = ({
  providerCompanyId,
  jobId,
  listViewType,
  setListViewType,
  setActiveTab,
  setSelectedOfferId,
  setOfficersCount,
  officersCount,
  fetchJobAndOfferMetadata
}) => {
  const [staffs, setStaffs] = useState([]);
  const [all, setAll] = useState([]);
  const [job, setJob] = useState({});
  const [hoveredStaff, setHoveredStaff] = useState({});
  const [loading, setLoading] = useState(true);
  const [exception, setException] = useState(false);
  const [positionsFilled, setPositionsFilled] = useState(false);
  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const locationParam = useLocation();
  const query = new URLSearchParams(locationParam.search);
  const view = query.get("view");

  const history = useHistory();

  // Add only necessary map styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .map-billboard-list {
        height: 100% !important;
        width: 100% !important;
      }
      
      .mapboxgl-map {
        height: 100% !important;
        width: 100% !important;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #E4E4E4;
        box-shadow: 8px 8px 40px 0px rgba(0, 0, 0, 0.05);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleNavigation = (viewType) => {
    setListViewType(viewType);
  };

  // ✅ Fetch Data on Mount & When Dropdown Changes
  useEffect(() => {
    fetchData(listViewType);
  }, [listViewType]);

  useEffect(() => {
    fetchData(listViewType);
  }, []);

  const fetchData = async (viewType) => {
    setLoading(true);
    try {
      let data;
      if (viewType === "recommended") {
        const response = await getRecommendation(providerCompanyId, jobId);
        data = response.data;
      } else {
        const response = await getOfficers(providerCompanyId, jobId);
        data = response.data;
      }

      if (data) {
        setOfficersCount(data.staffs.length);
        const staffsWithOffersField = data.staffs.map((staff) => ({
          ...staff,
          offerPrice: Math.round(data.job.hourlyPayRate * (1 - 0.35)),
          customerRatePercentage: 35,
          offerId: staff.offerId || null,
        }));

        const acceptedOffers = staffsWithOffersField.filter(
          (staff) =>
            staff.offer &&
            (staff.offer.status === "accepted" ||
              staff.offer.status === "completed" ||
              staff.offer.status === "paid")
        );
        
        const totalStaffsRequired = data.job.staffsQty;

        if (acceptedOffers.length >= totalStaffsRequired) {
          setStaffs(acceptedOffers);
          setAll([...acceptedOffers]);
          setJob({ ...data.job });
          setPositionsFilled(true);

          return;
        } else {
          setStaffs(staffsWithOffersField);
          setAll([...staffsWithOffersField]);
          setJob({ ...data.job });
        }
      }
    } catch (error) {
      console.error("Error Fetching Data:", error);
    }
    setLoading(false);
  };

  // Header component that's always displayed regardless of staffs availability
  const renderHeader = () => {
    if (!job || !Object.keys(job).length) return null;

    return (
      <div className="text-center mb-4">
        <div className="flex items-center justify-center flex-wrap gap-3">
          <div className="flex items-center gap-1 text-sm text-gray-700">
            <MapPin className="!text-lg" />
            {job.formattedAddress}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-700">
            <Shield className="!text-lg" />
            <b>{staffs.length} officers</b> 
            {listViewType === "closest" ? " within 100 miles" : " recommended"}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-700">
            <Banknote className="!text-lg" />
            <b>${job.hourlyPayRate}</b> per hour
          </div>
          <div className="flex items-center gap-1">
            <JobStatistics
              providerCompanyId={providerCompanyId}
              jobId={job._id}
              statistics={job.statistics}
              location={job.formattedAddress}
            />
          </div>
          <div className="ml-auto">
            <CustomDropdown
              title={
                <span className="text-xs text-gray-500">
                  {listViewType.charAt(0).toUpperCase() + listViewType.slice(1)}
                </span>
              }
            >
              {listViewType !== "closest" && (
                <CustomDropdownItem onClick={() => handleNavigation("closest")}>
                  <span className="font-medium text-xs text-gray-500">
                    Closest
                  </span>
                </CustomDropdownItem>
              )}
              {listViewType !== "recommended" && (
                <CustomDropdownItem onClick={() => handleNavigation("recommended")}>
                  <span className="font-medium text-xs text-gray-500">
                    Recommended
                  </span>
                </CustomDropdownItem>
              )}
            </CustomDropdown>
          </div>
        </div>
      </div>
    );
  };

  if (exception) return <Redirect to="/404" />;

  return (
    <div className="flex flex-col min-h-screen bg-dashboard-gradient">
      <div className="flex-1 flex flex-col">
        <div className="flex-none p-4">
          {/* Loading message */}
          {loading && (
            <div className="text-center py-4">
              <div className="text-gray-600">Loading data...</div>
            </div>
          )}

          {/* Show message for filled positions */}
          {!loading && positionsFilled && (
            <div className="mb-4">
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-center">
                <span>
                  All positions are currently filled. No further offers can
                  be sent at this time.
                </span>
              </div>
            </div>
          )}

          {/* Header - always show when job data is available */}
          {!loading && Object.keys(job).length > 0 && renderHeader()}
        </div>

        {/* Scrollable Content Area - Only the cards/officers list should scroll */}
        {!loading && Object.keys(job).length > 0 && (
          <div className="flex-1">
            <div className="min-h-96 px-4">
              {/* Closest view with sidebar */}
              {listViewType === "closest" && staffs.length > 0 && (
                <div className="flex gap-4" style={{ height: 'calc(100vh - 200px)' }}>
                  <div className="w-80 flex-shrink-0 overflow-y-auto">
                    <ListingSidebar
                      staffs={staffs}
                      all={all}
                      setStaffs={setStaffs}
                    />
                  </div>                    <div className="flex-1 flex gap-4">
                      <div className="flex-1 overflow-y-auto pr-2 pb-8">
                      <ListingDetails
                        providerCompanyId={providerCompanyId}
                        staffs={staffs}
                        setStaffs={setStaffs}
                        job={job}
                        hoveredStaff={hoveredStaff}
                        setHoveredStaff={setHoveredStaff}
                        setSelectedOfferId={setSelectedOfferId}
                        setActiveTab={setActiveTab}
                        setOfficersCount={setOfficersCount}
                        officersCount={officersCount}
                        fetchJobAndOfferMetadata={fetchJobAndOfferMetadata}
                      />
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <div className="h-96 sticky top-4">
                        <ListingDetailsMap
                          staffs={staffs}
                          job={job}
                          hoveredStaff={hoveredStaff}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Recommended view */}
              {listViewType === "recommended" && staffs.length > 0 && (
                <div className="flex gap-4" style={{ height: 'calc(100vh - 200px)' }}>
                  {/* Left Panel - Scrollable Recommendations */}
                  <div className="flex-1 overflow-y-auto pr-2 pb-8">
                    <RecommendedListingDetails
                      providerCompanyId={providerCompanyId}
                      staffs={staffs}
                      setStaffs={setStaffs}
                      job={job}
                      hoveredStaff={hoveredStaff}
                      setHoveredStaff={setHoveredStaff}
                      setSelectedOfferId={setSelectedOfferId}
                      setActiveTab={setActiveTab}
                      fetchJobAndOfferMetadata={fetchJobAndOfferMetadata}
                    />
                  </div>
                  
                  {/* Right Panel - Fixed Map */}
                  <div className="w-80 flex-shrink-0">
                    <div className="sticky top-4 h-96">
                      <ListingDetailsMap
                        staffs={staffs}
                        job={job}
                        hoveredStaff={hoveredStaff}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Message when no staffs are found */}
              {staffs.length === 0 && (
                <div className="text-center mt-4 p-4">
                  <div className="bg-blue-100 border border-blue-400 text-blue-700 px-3 py-2 rounded">
                    No officers are currently available for this job.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Listing;
